import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Card,
  DataStatus,
  EmptyStatus,
  openDialog,
  showNotification,
} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {isNil, omit} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {metadaApi, omneticApi} from '@dms/api/base';
import {InspectionType} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
  usePartialUpdateVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetOccupiedSpaceQuery} from '@dms/api/warehouse';
import {
  GetServiceHandOverApiResponse,
  PostServiceHandOverApiArg,
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInQuery,
  useGetServiceHandOverDefaultDriverApiQuery,
  useGetServiceHandOverQuery,
  usePatchServiceHandOverMutation,
  usePostServiceHandOverMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getCustomerName, handleApiError, useUpdateOdometer} from '@dms/shared';
import {SendAfterSalesEmailForm, SendAfterSalesCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, isNotNilOrEmpty, useBoolean} from 'shared/web';

import {CheckInHandOverForm} from '../../../../../components/CheckInHandOverForm/CheckInHandOverForm';
import {CheckInHandOverParameter} from '../../../../../components/CheckInHandOverParameter/CheckInHandOverParameter';
import {EmailSmsNotificationBoundary} from '../../../../../components/EmailSmsNotifications/EmailSmsNotificationBoundary';
import {NotificationCategory} from '../../../../../components/EmailSmsNotifications/types/EmailSmsNotificationForm';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {getTechnicalInspectionValidUntil} from '../../../../../utils/getTechnicalInspectionValidUntil';
import {parseTechnicalInspectionDate} from '../../../../../utils/parseTechnicalInspectionDate';
import {openMileageValidationDialogIfNeeded} from '../../../components/openMileageValidationDialogIfNeeded';
import {CreateInspectionDropdown} from './CreateInspectionDropdown';
import {Inspection} from './Inspection';

interface HandOverProps {
  vehicleId: string;
  isReadOnly?: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function HandOver(props: HandOverProps) {
  const dispatch = useDispatch();
  const {serviceCaseId: id} = useWorkshopUrl();
  const {
    data: handOver,
    isLoading: isHandOverLoading,
    isError: isHandOverError,
  } = useGetServiceHandOverQuery({serviceCaseId: id});
  // RTK reports isLoading while uninitialized; a selectFromResult here would blank driver fields.
  const {
    data: handoverDefaultDriver,
    isLoading: isHandoverDefaultDriverLoading,
    isError: isHandoverDefaultDriverError,
  } = useGetServiceHandOverDefaultDriverApiQuery(
    {serviceCaseId: id},
    {skip: isHandOverLoading || isNotNil(handOver)}
  );
  const {
    data: checkIn,
    isLoading: isCheckInLoading,
    isError: isCheckInError,
  } = useGetServiceCheckInQuery({serviceCaseId: id});
  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: id});
  const serviceCaseVehicleId = serviceCase?.vehicleId;

  const {data: vehicleMilage, isLoading: isVehicleMileageLoading} = useGetVehicleMileageQuery(
    isNotNilOrEmpty(serviceCaseVehicleId) ? {vehicleId: serviceCaseVehicleId} : skipToken
  );
  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    isNotNilOrEmpty(serviceCaseVehicleId) ? {vehicleId: serviceCaseVehicleId} : skipToken
  );
  const {data: parkingPlace} = useGetOccupiedSpaceQuery(
    isNotNilOrEmpty(serviceCaseVehicleId)
      ? {recordId: serviceCaseVehicleId, spaceType: 'VEHICLE'}
      : skipToken
  );

  const {data: vehicleCustomers} = useGetVehicleCustomerQuery(
    isNotNil(vehicle) ? {vehicleId: vehicle.id} : skipToken
  );

  const vehicleOperatorCustomerId = vehicleCustomers?.find(
    (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
  )?.customerId;

  const {data: vehicleOperatorCustomer} = useGetCustomerV2Query(
    isNotNilOrEmpty(vehicleOperatorCustomerId) ? {customerId: vehicleOperatorCustomerId} : skipToken
  );

  const [handleUpdateOdometer] = useUpdateOdometer(serviceCase?.vehicleId);
  const [postServiceHandOver] = usePostServiceHandOverMutation();
  const [patchServiceHandOver, {isLoading: isPatchingServiceHandOver}] =
    usePatchServiceHandOverMutation();
  const [partialUpdateVehicle] = usePartialUpdateVehicleMutation();

  const [isEditing, setEditing, cancelEditing] = useBoolean();
  const inspectionId = handOver?.inspectionId?.[0];

  const handleSaveHandOver = async (data: PostServiceHandOverApiArg['body']) => {
    if (isNotNil(data) && inspectionId) {
      data.inspectionId = [inspectionId];
    }

    const technicalInspectionValidUntil = getTechnicalInspectionValidUntil(
      data?.technicalInspection
    );

    if (isNotNil(technicalInspectionValidUntil) && isNotNil(vehicle)) {
      partialUpdateVehicle({
        vehicleId: vehicle.id,
        patchVehicleRequestBody: {
          state: {technicalInspectionValidUntil},
        },
      })
        .unwrap()
        .catch(handleApiError);
    }
    const request = handOver
      ? patchServiceHandOver({body: data, serviceCaseId: id})
      : postServiceHandOver({body: data, serviceCaseId: id});

    await request
      .unwrap()
      .then((response) => {
        openMileageValidationDialogIfNeeded(response);

        if (!handOver) {
          dispatch(
            omneticApi.util.invalidateTags(['storageOccupiedSpace', 'storageOccupiedSpaces'])
          );
        }

        const isNewEntity = !handOver;
        if (isNewEntity || (vehicleMilage?.mileage ?? 0) < (data?.mileage ?? 0)) {
          handleUpdateOdometer({
            recordId: id,
            milage: data?.mileage ?? 0,
            action: 'service',
            recordTitle: `${vehicle?.title} - ${getCustomerName(vehicleOperatorCustomer)} (${i18n.t('entity.workshop.labels.handover')})`,
          });
        }

        cancelEditing();
        dispatch(metadaApi.util.invalidateTags(['serviceCaseByVehicleCount']));
      })
      .catch(handleApiError);
  };
  const isLoading =
    isHandOverLoading ||
    isCheckInLoading ||
    isHandoverDefaultDriverLoading ||
    isServiceCaseLoading ||
    isVehicleLoading ||
    isVehicleMileageLoading;
  const isError =
    isHandOverError ||
    isCheckInError ||
    isHandoverDefaultDriverError ||
    isServiceCaseError ||
    isVehicleError;
  const isFormView = !handOver || handOver?.state === 'SCHEDULED' || isEditing;

  const cardActions = buildArray<Action>().whenNot(isFormView || props.isReadOnly, {
    type: 'button',
    variant: 'link',
    leftIcon: 'image/edit',
    title: i18n.t('general.actions.edit'),
    onClick: setEditing,
    'data-testid': testIds.workshop.serviceCaseDetail('handOverEdit'),
  });

  const parkingPlaceName =
    `${parkingPlace?.prefix ?? ''}${parkingPlace?.number ?? ''}` || undefined;

  const vehicleTechnicalInspection = vehicle?.state?.technicalInspectionValidUntil;

  const defaultValues: GetServiceHandOverApiResponse = {
    ...omit(
      ['scheduledDate', 'completedDate', 'consentToTestDrive', 'inspectionId'],
      checkIn ?? {}
    ),
    driver: {
      id: handoverDefaultDriver?.driverId,
      contactId: handoverDefaultDriver?.driverContactId,
      name: `${handoverDefaultDriver?.driverFirstname ?? ''} ${handoverDefaultDriver?.driverLastname ?? ''}`.trim(),
      email: handoverDefaultDriver?.driverEmail || null,
      phoneNumber: handoverDefaultDriver?.driverPhoneNo
        ? {
            countryCode: handoverDefaultDriver?.driverPhoneCountryCode || '',
            number: handoverDefaultDriver?.driverPhoneNo || '',
            prefix: handoverDefaultDriver?.driverPhonePrefix || '',
          }
        : null,
      identityCard: handoverDefaultDriver?.driverIdentityCard || null,
    },
    // Only let the vehicle override the check-in's value when it is actually usable,
    // otherwise a corrupt vehicle date would discard a valid one the check-in recorded.
    technicalInspection: isNotNil(parseTechnicalInspectionDate(vehicleTechnicalInspection))
      ? vehicleTechnicalInspection
      : checkIn?.technicalInspection,
  };

  return (
    <VStack spacing={4}>
      <Card title={i18n.t('general.labels.basicInformation')} actions={cardActions}>
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          data-testid={testIds.workshop.serviceCaseDetail('handOverstatus')}
        >
          <Show when={isFormView}>
            <CheckInHandOverForm
              data-testid={testIds.workshop.serviceCaseDetail('handOvereditForm')}
              defaultValues={handOver ?? defaultValues}
              fuelType={vehicle?.fuelType}
              onSubmit={handleSaveHandOver}
              isEditing={isEditing}
              isReadOnly={props.isReadOnly}
              onEditDiscard={cancelEditing}
            />
          </Show>
          <Hide when={isFormView}>
            <CheckInHandOverParameter
              data={handOver}
              parkingPlace={parkingPlaceName}
              fuelType={vehicle?.fuelType}
              data-testid={testIds.workshop.serviceCaseDetail('handOverlist')}
            />
          </Hide>
        </DataStatus>
      </Card>
      <Hide when={props.isReadOnly}>
        <Show when={isNil(inspectionId)}>
          <Card
            title={i18n.t('page.Inspection.labels.title')}
            actions={[
              {
                type: 'custom',
                component: (
                  <CreateInspectionDropdown
                    vehicleId={props.vehicleId}
                    serviceCaseTypeId={serviceCase?.serviceCaseTypeId ?? ''}
                    inspectionType={InspectionType.HANDOVER}
                    isDisabled={isNil(handOver)}
                    isLoading={isPatchingServiceHandOver}
                    onInspectionCreated={async (inspectionId) => {
                      await patchServiceHandOver({
                        body: {...handOver, inspectionId: [inspectionId]},
                        serviceCaseId: id,
                      })
                        .unwrap()
                        .catch(handleApiError);
                    }}
                    data-testid={testIds.workshop.serviceCaseDetail('createHandoverInspection')}
                  />
                ),
              },
            ]}
          >
            <EmptyStatus headline={i18n.t('entity.order.actions.handOverEmpty')} />
          </Card>
        </Show>
      </Hide>
      <Show when={!!handOver && !!inspectionId}>
        <Inspection
          inspectionId={inspectionId || ''}
          vehicleId={props.vehicleId}
          isReadOnly={props.isReadOnly}
          isCompletionDisabled={isNil(handOver?.inspectionId?.[0])}
          data-testid={testIds.workshop.serviceCaseDetail('handoverInspection')}
          onInspectionDeleted={() => {
            if (!handOver) {
              return;
            }

            const {inspectionId: _, ...rest} = handOver;

            patchServiceHandOver({serviceCaseId: id, body: rest}).unwrap().catch(handleApiError);
          }}
          onSendEmailNotification={() => {
            openDialog(
              <SendAfterSalesEmailForm
                serviceCaseId={id}
                defaultValues={{defaultCategory: 'service-customer_handover'}}
              />,
              {
                id: 'email-sms-notification-dialog',
                withAdditionalFooter: true,
                size: 'large',
                title: i18n.t('general.actions.sendEmail'),
              }
            );
          }}
          onSendSmsNotification={() => {
            if (isNil(serviceCase?.customerId)) {
              showNotification.error(i18n.t('general.notifications.notFound'));
              return;
            }

            openDialog(
              <SendAfterSalesCustomerSmsForm
                serviceCaseId={id}
                recordId={id}
                resourceId={EntityResourceIds.serviceCase}
              />,
              {
                title: i18n.t('general.actions.sendSms'),
                size: 'large',
                scrollBehavior: 'outside',
              }
            );
          }}
          onSendEmailSmsNotification={() => {
            openDialog(
              <EmailSmsNotificationBoundary
                serviceCaseId={id}
                dialogId="email-sms-notification-dialog-handover"
                data-testid={testIds.workshop.serviceCaseDetail('emailSmsNotificationHandover')}
                defaultCategory={NotificationCategory.HANDOVER}
              />,
              {
                id: 'email-sms-notification-dialog-handover',
                size: 'large',
                title: i18n.t('entity.order.actions.sendEmailSms'),
              }
            );
          }}
        />
      </Show>
    </VStack>
  );
}
